import React, { useState } from "react";
import { CheckCircle2, XCircle, RotateCcw, Award } from "lucide-react";
import { api } from "../lib/api";

export default function QuizForm({ quiz, courseSlug, lessonId, onPassed }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const setAnswer = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = Object.entries(answers).map(([question_id, val]) => {
        if (typeof val === "string" && val.startsWith("__text__:")) {
          return { question_id, text: val.slice("__text__:".length) };
        }
        return { question_id, selected: Array.isArray(val) ? val : [val] };
      });
      const r = await api.post(`/learn/${courseSlug}/${lessonId}/quiz/attempt`, { answers: payload });
      setResult(r.data);
      if (r.data.passed) onPassed?.(r.data);
    } catch (err) {
      console.error("Quiz attempt failed", err);
    } finally {
      setBusy(false);
    }
  };

  const retake = () => { setAnswers({}); setResult(null); };

  const feedbackByQ = result ? Object.fromEntries(result.per_question.map((p) => [p.question_id, p])) : {};

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="quiz-form">
      {result && (
        <div
          className={`border p-5 ${
            result.passed
              ? "bg-[#10B981]/10 border-[#10B981]/40 text-[#10B981]"
              : "bg-fc-red/10 border-fc-red/40 text-fc-red"
          }`}
          data-testid="quiz-result-banner"
        >
          <div className="flex items-center gap-3">
            {result.passed ? <Award size={22} /> : <XCircle size={22} />}
            <div>
              <p className="font-black text-lg">
                {result.passed ? "Passed!" : "Not quite."} {result.percentage}% ({result.score}/{result.total})
              </p>
              <p className="text-xs opacity-80">
                You need {result.passing_score}% to pass. {result.xp_awarded > 0 && `+${result.xp_awarded} XP earned.`}
              </p>
            </div>
          </div>
        </div>
      )}

      {quiz.questions.map((q, idx) => {
        const fb = feedbackByQ[q.id];
        const correctIds = new Set(fb?.correct_option_ids || []);
        const selectedIds = new Set(fb?.selected || []);
        return (
          <div key={q.id} className="bg-fc-navy border border-white/10 p-5" data-testid={`quiz-question-${idx}`}>
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-white/40 mb-2">Question {idx + 1} · {q.type.replace("_", " ")}</p>
            <p className="text-white font-bold mb-4">{q.prompt}</p>

            {q.type === "fill_blank" ? (
              <input
                type="text"
                disabled={!!result}
                value={(answers[q.id] || "").replace(/^__text__:/, "")}
                onChange={(e) => setAnswer(q.id, `__text__:${e.target.value}`)}
                className="w-full bg-[#060A24] border border-white/10 focus:border-fc-red text-white px-3 py-2 outline-none"
                placeholder="Your answer"
                data-testid={`quiz-q${idx}-text`}
              />
            ) : (
              <div className="space-y-2">
                {q.options.map((opt) => {
                  const isMulti = q.type === "multiple_select";
                  const cur = answers[q.id] || (isMulti ? [] : null);
                  const selected = isMulti ? cur.includes(opt.id) : cur === opt.id;
                  const showCorrect = !!result && correctIds.has(opt.id);
                  const showWrong = !!result && selectedIds.has(opt.id) && !correctIds.has(opt.id);
                  return (
                    <label
                      key={opt.id}
                      className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                        showCorrect ? "border-[#10B981] bg-[#10B981]/10"
                        : showWrong ? "border-fc-red bg-fc-red/10"
                        : selected ? "border-fc-red bg-fc-red/5"
                        : "border-white/10 hover:border-white/30"
                      }`}
                      data-testid={`quiz-q${idx}-opt-${opt.id}`}
                    >
                      <input
                        type={isMulti ? "checkbox" : "radio"}
                        name={`q-${q.id}`}
                        disabled={!!result}
                        checked={selected}
                        onChange={() => {
                          if (isMulti) {
                            const next = selected
                              ? cur.filter((x) => x !== opt.id)
                              : [...cur, opt.id];
                            setAnswer(q.id, next);
                          } else {
                            setAnswer(q.id, opt.id);
                          }
                        }}
                        className="mt-1 accent-[#FF4343]"
                      />
                      <span className="text-white/90 text-sm flex-1">{opt.text}</span>
                      {showCorrect && <CheckCircle2 size={16} className="text-[#10B981]" />}
                      {showWrong && <XCircle size={16} className="text-fc-red" />}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <div className="flex gap-3">
        {!result ? (
          <button type="submit" disabled={busy} className="btn-fc-red" data-testid="quiz-submit-btn">
            {busy ? "Grading…" : "Submit Quiz"}
          </button>
        ) : (
          <button type="button" onClick={retake} className="btn-fc-outline" data-testid="quiz-retake-btn">
            <RotateCcw size={14} /> Retake
          </button>
        )}
      </div>
    </form>
  );
}
